<template>
  <component
    :is="interactive ? 'button' : 'div'"
    :type="interactive ? 'button' : null"
    :class="['accessory-thumb', { 'accessory-thumb--empty': !showImage }]"
    @click="$emit('click')"
  >
    <img v-if="showImage" :src="src" :alt="alt" loading="lazy" @error="failed = true" />
    <span v-else>{{ compact ? '—' : i18n.t('/AccessoryCatalog/NoPhoto') }}</span>
  </component>
</template>

<script>
export default {
  props: {
    src: {
      type: String,
      default: null,
    },
    alt: {
      type: String,
      default: '',
    },
    compact: {
      type: Boolean,
      default: false,
    },
    /**
     * The thumb is a control the user operates (the gallery strip), not a
     * picture next to one. A div with a click handler is unreachable by
     * keyboard, so those call sites get a real button — with the caller's
     * `aria-label`/`aria-pressed` falling through onto it.
     */
    interactive: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['click'],
  data() {
    return {
      failed: false,
      i18n: window.VUE_CLI_EXPORTS.plugins.i18n,
    }
  },
  computed: {
    showImage() {
      return !!this.src && !this.failed
    },
  },
  watch: {
    // A new photo deserves its own attempt; the previous one failing says
    // nothing about this URL
    src() {
      this.failed = false
    },
  },
}
</script>

<style lang="scss" scoped>
// There is no global font-size scale in styles/variables, and the placeholder
// is the only text this component draws.
$placeholder-size: 10px;

.accessory-thumb {
  position: relative;
  display: grid;
  place-items: center;
  background: $gray-lightest;
  background-image: repeating-linear-gradient(
    135deg,
    rgba(0, 0, 0, 0.05) 0 6px,
    transparent 6px 13px
  );
  border: 1px solid $gray-lightest;
  border-radius: 6px;
  overflow: hidden;

  span {
    font-family: $font-family-mono;
    font-size: $placeholder-size;
    font-weight: 600;
    line-height: 1.25;
    letter-spacing: 0.05em;
    color: $gray;
    text-align: center;
    padding: 0 4px;
  }

  img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
    background: $white;
  }

  &--empty {
    background-image: none;
    border-style: dashed;
    border-color: $gray-light;
  }
}

// The button's own chrome would otherwise fight the tile it has to look like
button.accessory-thumb {
  padding: 0;
  font: inherit;
  cursor: pointer;

  // The app sets a global `*:focus { outline: 1px dotted }` in styles/app/_accessibility.scss,
  // which paints on mouse click too. Suppress it here so only keyboard focus is indicated.
  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: 2px solid var(--color-primary);
    outline-offset: 2px;
  }
}
</style>
